'use client';

import { c as _c } from "react/compiler-runtime";
import { useClaimFaucet } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useEffect, useRef } from 'react';
import { FAUCET_COOLDOWN_FALLBACK_SECONDS } from '@/config/faucet';
import { TOAST_IDS } from '@/config/toast-ids';
import { onEvent } from '@/events';
import type { FaucetClaimSource } from '@/events/faucet';
import { useFormattedCurrency } from '@/lib/currency';
import { cooldownDisplay, extractFaucetCooldownSeconds, isFaucetCooldownError } from '@/lib/faucet/cooldown';
import { devnetExplorerTxUrl } from '@/lib/faucet/explorer';
import { reportError } from '@/lib/telemetry';
import { logger } from '@/lib/telemetry/logger';
import { fireRewardBurst } from '@/lib/ui/confetti';
import { dismissToast, showToast } from '@/lib/ui/toast';
import { useInvalidateWalletBalances } from '@/queries/wallet-balance';
export interface FaucetClaimResult {
  signature: string;
  amount: number;
  walletAddress: string;
  cooldownSeconds: number;
}
type FaucetClaimPayload = Omit<FaucetClaimResult, 'cooldownSeconds'> & {
  cooldownSeconds?: number;
};

// Stable identity — passed by reference so a fresh options/meta object isn't
// allocated on every render of every faucet surface. `meta.skipGlobalErrorHandler`
// opts this mutation out of the global MutationErrorListener funnel.
const FAUCET_CLAIM_OPTIONS = {
  meta: {
    skipGlobalErrorHandler: true
  }
} as const;

// ALLOW-UNKNOWN: SDK-generated faucet mutation response is untyped (`unknown`); narrowed here at the boundary.
function unwrapFaucetClaim(raw: unknown): FaucetClaimPayload {
  if (raw !== null && typeof raw === 'object' && 'data' in raw) {
    return (raw as {
      data: FaucetClaimPayload;
    }).data;
  }
  return raw as FaucetClaimPayload;
}
function isValidCooldownSeconds(value: number | undefined): value is number {
  return value !== undefined && Number.isInteger(value) && value > 0;
}

/** Outcome of a failed claim, handed to the caller's `onFailed` so a richer
 *  surface (e.g. the faucet modal) can render an inline state in addition to
 *  the toast this hook always shows. */
export interface FaucetClaimFailure {
  isCooldown: boolean;
  cooldownSeconds: number | null;
}
export interface FaucetClaimCallbacks {
  onClaimed?: (result: FaucetClaimResult) => void;
  onFailed?: (failure: FaucetClaimFailure) => void;
}
export interface FaucetClaimOptions extends FaucetClaimCallbacks {
  /** Whole-USDC grant to mint, so the minted amount is worth the local-currency
   *  figure the UI showed (e.g. £1,000 → ~1,351 USDC). Clamped server-side; omit
   *  for the fixed default grant. */
  amountUsdc?: number;
}
export interface UseFaucetClaim {
  /** Claim devnet test cash for `walletAddress`. Owns the entire toast
   *  lifecycle (loading → success | error) under a single toast id; callbacks
   *  let a richer surface mirror the outcome inline. */
  claim: (walletAddress: string, opts?: FaucetClaimOptions) => void;
  isPending: boolean;
}

/**
 * Single source of truth for the devnet faucet claim UX, shared by the
 * portfolio banner and the onboarding stage.
 *
 * - Shows a persistent "Claiming test funds…" loading toast that the success
 *   or error toast replaces in place (same `TOAST_IDS.FAUCET_CLAIM` id).
 * - On cooldown, renders a "come back in X" message derived from the remaining
 *   seconds the backend reports.
 * - Opts out of the global `MutationErrorListener` funnel via
 *   `meta.skipGlobalErrorHandler` so the funnel's generic toast doesn't
 *   double-fire on top of this one.
 */
export function useFaucetClaim(source) {
  const $ = _c(15);
  const t = useTranslations("faucet");
  const tTx = useTranslations("tx");
  const faucetClaim = useClaimFaucet(FAUCET_CLAIM_OPTIONS);
  const invalidateWalletBalances = useInvalidateWalletBalances();
  const {
    formatBalance
  } = useFormattedCurrency();
  const claimPendingRef = useRef(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => () => {
      if (claimPendingRef.current) {
        dismissToast(TOAST_IDS.FAUCET_CLAIM);
      }
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] !== t) {
    t2 = function cooldownMessage(seconds) {
      const display = cooldownDisplay(seconds);
      if (display.unit === "hours") {
        return t("cooldown_hours", {
          hours: display.value
        });
      }
      if (display.unit === "minutes") {
        return t("cooldown_minutes", {
          minutes: display.value
        });
      }
      return t("cooldown_soon");
    };
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const cooldownMessage = t2;
  let t3;
  if ($[4] !== cooldownMessage || $[5] !== faucetClaim || $[6] !== formatBalance || $[7] !== invalidateWalletBalances || $[8] !== source || $[9] !== t || $[10] !== tTx) {
    t3 = function claim(walletAddress, opts) {
      onEvent("faucet_claim_initiated", {
        source,
        walletAddress
      });
      showToast({
        id: TOAST_IDS.FAUCET_CLAIM,
        message: t("claiming"),
        variant: "loading",
        duration: Number.POSITIVE_INFINITY
      });
      claimPendingRef.current = true;
      faucetClaim.mutate({
        walletAddress,
        ...(opts?.amountUsdc !== undefined ? {
          amountUsdc: opts.amountUsdc
        } : {})
      }, {
        onSuccess: raw => {
          claimPendingRef.current = false;
          const payload = unwrapFaucetClaim(raw);
          const cooldownSeconds = isValidCooldownSeconds(payload.cooldownSeconds) ? payload.cooldownSeconds : FAUCET_COOLDOWN_FALLBACK_SECONDS;
          if (!isValidCooldownSeconds(payload.cooldownSeconds)) {
            logger.warn("faucet.claim_success_cooldown_missing", {
              source
            });
          }
          const data = {
            ...payload,
            cooldownSeconds
          };
          invalidateWalletBalances(data.walletAddress);
          fireRewardBurst();
          showToast({
            id: TOAST_IDS.FAUCET_CLAIM,
            message: tTx("toast.claimAddedToWallet", {
              amount: formatBalance(data.amount, "clean")
            }),
            variant: "success",
            action: {
              label: t("view_on_explorer"),
              onClick: () => {
                window.open(devnetExplorerTxUrl(data.signature), "_blank", "noopener");
              }
            }
          });
          onEvent("faucet_claim_succeeded", {
            source,
            walletAddress: data.walletAddress,
            amount: data.amount,
            signature: data.signature
          });
          opts?.onClaimed?.(data);
        },
        onError: error => {
          claimPendingRef.current = false;
          const isCooldown = isFaucetCooldownError(error);
          const cooldownSeconds_0 = isCooldown ? extractFaucetCooldownSeconds(error) : null;
          showToast({
            id: TOAST_IDS.FAUCET_CLAIM,
            message: isCooldown ? cooldownMessage(cooldownSeconds_0) : t("generic_error"),
            variant: "error"
          });
          onEvent("faucet_claim_failed", {
            source,
            walletAddress,
            reason: isCooldown ? "cooldown" : "other"
          });
          if (!isCooldown) {
            reportError(error, {
              component: "useFaucetClaim",
              context: "faucet_claim",
              url: typeof window !== "undefined" ? window.location.href : ""
            });
          }
          opts?.onFailed?.({
            isCooldown,
            cooldownSeconds: cooldownSeconds_0
          });
        }
      });
    };
    $[4] = cooldownMessage;
    $[5] = faucetClaim;
    $[6] = formatBalance;
    $[7] = invalidateWalletBalances;
    $[8] = source;
    $[9] = t;
    $[10] = tTx;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const claim = t3;
  let t4;
  if ($[12] !== claim || $[13] !== faucetClaim.isPending) {
    t4 = {
      claim,
      isPending: faucetClaim.isPending
    };
    $[12] = claim;
    $[13] = faucetClaim.isPending;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}